import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Restaurant } from '../models/restaurant.model';
import { OpenHour } from '../models/open-hour.model';
import { Category } from '../models/category.model';
import { Product } from '../models/product.model';
import { Order } from '../models/order.model';
import { OpenDateTime } from '../models/open-date-time.model';
import { DeliveryZoneInfo } from '../models/delivery-zone-info.model';
import { Page } from '../models/page.model';
import { HttpClient } from '@angular/common/http';
import { CreditCard } from '../models/credit-card.model';

@Injectable()
export class ShopService {

  restaurants = new BehaviorSubject<Array<Restaurant>>([]);
  restaurant = new BehaviorSubject<Restaurant>(null);
  openHours = new BehaviorSubject<Array<OpenHour>>([]);
  orderType = new BehaviorSubject<string>(null); // 'delivery' or 'takeout'
  categories = new BehaviorSubject<Array<Category>>([]);
  category = new BehaviorSubject<Category>(null);
  products = new BehaviorSubject<Array<Product>>([]);
  basketProducts = new BehaviorSubject<Array<Product>>([]);
  order = new BehaviorSubject<Order>(new Order());
  selectedDateTime = new BehaviorSubject<string>(null);
  selectedDate = new BehaviorSubject<Date>(null);
  selectedTime = new BehaviorSubject<string>(null);
  openDateTimes = new BehaviorSubject<Array<OpenDateTime>>([]);
  deliveryZoneInfo = new BehaviorSubject<DeliveryZoneInfo>(null);
  pages = new BehaviorSubject<Array<Page>>([]);
  card = new BehaviorSubject(null);

  // allergene
  signs = [
    { color: '#DE323B', name: 'HOT' },
    { color: '#981810', name: 'VERY_HOT' },
    { color: '#82C746', name: 'VEGETARIAN' },
    { color: '#1C9963', name: 'VEGAN' },
    { color: '#FFAF2E', name: 'CARBOHYDRATE_REDUCED' },
    { color: '#2AB9EA', name: 'UNTIL_22_O_CLOCK' },
  ];

  constructor(
    private apiService: ApiService,
    private http: HttpClient
  ) { }

  initRestaurant(restaurantId: number) {
    const restaurant = this.restaurants.value.filter((r) => r.restaurantId === restaurantId)[0];
    this.restaurant.next(restaurant);
  }

  getPages() {
    return this.http.get('http://cms.nooba.ch/wp-json/wp/v2/pages/');
  }

  getRestaurants() {
    return this.apiService.get('/api/Restaurants/All', 'json', false).pipe(
      map(
        (data: Array<Restaurant>) => {
          this.restaurants.next(data);

          return data;
        }
      )
    );
  }

  getOpenHours() {
    return this.apiService.get('/api/Restaurants/OpenHours/EditMode', 'json', false).pipe(
      map(
        (data: Array<OpenHour>) => {
          this.openHours.next(data);

          return data;
        }
      )
    );
  }

  getCategories() {
    if (this.orderType.value === 'delivery' && !this.deliveryZoneInfo.value) { return throwError('no delivery restaurantId'); }
    const c = this.orderType.value === 'delivery' ? 3 : 6;
    const r = this.orderType.value === 'delivery' ? this.deliveryZoneInfo.value.restaurantId : this.restaurant.value.restaurantId;
    return this.apiService.get(`/api/Categories?clienttypeid=${c}&restaurantId=${r}`, 'json', false).pipe(
      map(
        (data: Array<Category>) => {
          this.categories.next(data);

          return data;
        }
      )
    );
  }

  getProducts() {
    const c = this.orderType.value === 'delivery' ? 3 : 6;
    const r = this.restaurant.value.restaurantId;
    const cat = this.category.value.categoryId;
    return this.apiService.get(`/api/Products?clienttypeid=${c}&restaurantId=${r}&categoryid=${cat}`, 'json', false).pipe(
      map(
        (data: Array<Product>) => {
          this.products.next(data);

          return data;
        }
      )
    );
  }

  addProductToBasket(p: Product) {
    const order = this.order.value;
    let exists = false;
    if (!order.orderItems) {
      order.orderItems = [];
    }
    for (let i = 0; i < order.orderItems.length; i++) {
      if (order.orderItems[i].productId === p.productId) {
        order.orderItems[i].quantity += 1;
        exists = true;
      }
    }
    if (!exists) {
      order.orderItems.push({
        productId: p.productId,
        quantity: 1
      });
    }
    this.order.next(order);
    this.calculateOrder().subscribe((c) => {
      // calc done
    });
  }

  calculateOrder() {
    const order = this.order.value;
    const clienttypeid = this.orderType.value === 'delivery' ? 3 : 6;
    order.clientTypeId = clienttypeid;
    order.restaurantId = this.restaurant.value.restaurantId;
    const items = order.orderItems.filter((o) => o.isAvailable || o.isAvailable !== false);
    order.orderItems = items;
    if (order.orderItems.length < 1) {
      return;
    }
    return this.apiService.post('/api/Orders/Calculate', order, 'json', false).pipe(
      map(
        (data: Order) => {
          this.order.next(data);

          return data;
        }
      ),
      catchError(
        (error) => {
          this.order.next(error.error);
          return error;
        }
      )
    );
  }

  getDeliveryZoneInfoByGooglePlaces(address_components) {
    const body = {
      html_attributions: [],
      results: []
    };
    body.results.push(address_components);
    return this.apiService.post('api/Orders/Delivery/GeoCode', body, 'json', false).pipe(
      map(
        (data: DeliveryZoneInfo) => {
          this.deliveryZoneInfo.next(data);

          return data;
        }
      )
    );
  }

  getOpenDateTimes() {
    const c = this.orderType.value === 'delivery' ? 3 : 6;
    const r = this.restaurant.value.restaurantId;
    return this.apiService.get(`/api/Restaurants/OpenHours?clienttypeid=${c}&restaurantid=${r}&showDays=7&currentTime`, 'json', false).pipe(
      map(
        (data: Array<OpenDateTime>) => {
          this.openDateTimes.next(data);

          return data;
        }
      )
    );
  }

  sendOrder() {
    const order = this.order.value;
    order.deliveryTime = this.selectedDateTime.value;
    const c = this.orderType.value === 'delivery' ? 3 : 6;
    if (this.card.value) {
      order.paymentInfo = this.card.value;
    }
    if (c === 3) {
      order.deliveryZoneId = this.deliveryZoneInfo.value.deliveryZoneId;
    }
    return this.apiService.post('/api/Orders', order, 'json', false).pipe(
      map(
        (data) => {
          return data;
        }
      ),
      catchError(
        (error) => {
          return error;
        }
      )
    );
  }
}
